import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

/**
 * Zustand der ⌘K-Palette, damit mehrere Stellen sie öffnen können.
 *
 * Vorher verschickte der Suchknopf in der Kopfzeile ein **künstliches
 * Tastaturereignis**, um die Palette zu öffnen. Das war ein Kunstgriff: er
 * täuscht eine Nutzereingabe vor, ist in Tests nicht nachvollziehbar und bricht,
 * sobald sich das Tastenkürzel ändert. Hier steht der Zustand offen zugänglich –
 * Knopf und Tastatur nutzen denselben Weg.
 */
interface PaletteZustand {
  offen: boolean
  oeffnen: () => void
  schliessen: () => void
  umschalten: () => void
}

const Kontext = createContext<PaletteZustand | null>(null)

export function PaletteProvider({ children }: { children: ReactNode }) {
  const [offen, setOffen] = useState(false)

  const oeffnen = useCallback(() => setOffen(true), [])
  const schliessen = useCallback(() => setOffen(false), [])
  const umschalten = useCallback(() => setOffen((o) => !o), [])

  const wert = useMemo(
    () => ({ offen, oeffnen, schliessen, umschalten }),
    [offen, oeffnen, schliessen, umschalten],
  )

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>
}

export function usePalette(): PaletteZustand {
  const wert = useContext(Kontext)
  if (!wert) throw new Error('usePalette braucht einen PaletteProvider')
  return wert
}
